import * as React from "react"
import { RouteComponentProps } from "@gatsbyjs/reach-router"
import { MdLaunch } from "react-icons/md"
import {
  ThemeCss,
  EmptyState,
  EmptyStatePrimaryAction,
  Spacer,
  Heading,
  Text,
  Button,
  Link,
} from "gatsby-interface"
import { createSite as text } from "@modules/locales/default.js"
import {
  getPathToOrgDetailsTab,
  getPathToOrgSettings,
} from "@modules/organization/shared/utils"
import { useAvailableSourceProvidersWithVcsConfigQuery } from "@modules/site/create/Import/queries.generated"
import {
  SourceControlProviderIcon,
  useSupportedSourceControlProviders,
} from "@modules/sourceControlProvider"
import { useOnPremisesGitConnectionsQuery } from "@modules/organization/queries.generated"
import { useOrganizationPermissionsInfoQuery } from "@modules/organization/queries.generated"
import { OrgDetailsTab } from "@modules/organization/details/constants"
import { SiteCreationWizardHeader } from "@modules/site/create/Import/components/SiteCreationWizardHeader"
import { SourceProviderSelector } from "@modules/site/create/Import/components/SourceProviderSelector"
import { SiteCreationOrganizationPicker } from "@modules/site/create/Import/components/SiteCreationOrganizationPicker"
import StarterPicker from "@modules/site/create/Provision/components/StarterPicker"
import { SourceControlProvider } from "@modules/graphql/types"
import { processSignupAttempt } from "@modules/auth/utils"
import { getUserId } from "@modules/analytics/utils"
import {
  getPathToCreateSite,
  isSelfHosted,
  mapProviderToLoginOption,
} from "@modules/site/create/shared/utils"
import { StandardSingleColumn } from "@modules/ui/layouts/Containers"
import useSelectedProvider from "@modules/site/create/shared/hooks/useSelectedProvider"
import { usePersistentState } from "@modules/toolkit/hooks/usePersistentState"
import { useFlags } from "@modules/featureFlags"
import { FormattedMessage } from "@modules/locales"

const positioningCss: ThemeCss = theme => ({
  maxWidth: `90%`,
  margin: `0 auto ${theme.space[10]}`,

  [theme.mediaQueries.desktop]: {
    maxWidth: `60rem`,
  },
})

const siteCardsContainerCss: ThemeCss = theme => ({
  display: "grid",
  width: "100%",
  gridTemplateColumns: "1fr",
  gridGap: theme.space[9],
  [`@media (min-width: 1100px)`]: {
    // prevents unwanted wrap around - needs custom breakpoint
    gridTemplateColumns: "1fr 1fr",
  },
})

export const siteCardContainerCss: ThemeCss = theme => ({
  // this is used for both the StarterPicker and the Repository Picker - styles here will affect both components
  position: "relative",
  padding: theme.space[8],
  display: `flex`,
  flexDirection: `column`,
  minHeight: "453px",
  borderRadius: theme.radii[3],
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows.floating,
  [`@media (min-width: 1100px)`]: {
    maxHeight: `550px`,
    // prevents unwanted wrap around - needs custom breakpoint
    [`@media (min-height: 850px)`]: {
      // we only want to expand after the screen size gets to a certain height
      height: `65vh`,
      maxHeight: `680px`,
    },
  },
})
export const flexGrowCss: ThemeCss = _theme => ({
  display: `flex`,
  flexDirection: `column`,
  flexGrow: 1,
})

const importSiteCardContentCss: ThemeCss = theme => [
  flexGrowCss(theme),
  {
    marginTop: theme.space[7],
    padding: theme.space[10],
    width: "100%",
    borderRadius: theme.radii[2],
    backgroundColor: theme.colors.grey[5],
    overflowY: `auto`,
  },
]

const importSiteCardSelectedProviderCss: ThemeCss = theme => [
  flexGrowCss(theme),
  {
    marginTop: theme.space[4],
    backgroundColor: theme.colors.white,
    width: "100%",
  },
]

export type SiteCreationTypePickerProps = RouteComponentProps<{
  organizationId: string
}>

export function SiteCreationTypePicker({
  organizationId,
}: SiteCreationTypePickerProps) {
  const {
    data: sourceProviders,
  } = useAvailableSourceProvidersWithVcsConfigQuery({
    variables: {
      workspaceId: organizationId,
    },
  })

  const [selectedProvider, setSelectedProvider] = useSelectedProvider()
  const [vcsConfigId, setVcsConfigId] = usePersistentState<string>(
    "gatsby:providerConfigId",
    ``
  )
  const { flags } = useFlags()
  const organizationPermissionsInfo = useOrganizationPermissionsInfoQuery({
    variables: { id: organizationId },
    skip: !organizationId,
  })

  const supportedProviders = useSupportedSourceControlProviders()
  const { data: onPremisesConnectionsData } = useOnPremisesGitConnectionsQuery({
    variables: {
      workspaceId: organizationId,
    },
  })
  const onPremisesConnections =
    onPremisesConnectionsData?.publicVcsConfigsByWorkspace

  const showOrganizationPicker =
    selectedProvider &&
    supportedProviders.includes(selectedProvider) &&
    sourceProviders?.availableSourceProvidersWithVcsConfig?.find(provider => {
      if (isSelfHosted(provider.source)) {
        return (
          vcsConfigId === provider.vcsConfigId &&
          provider.source === selectedProvider
        )
      } else {
        return provider.source === selectedProvider
      }
    })?.connected

  if (
    !organizationId ||
    !sourceProviders ||
    organizationPermissionsInfo.loading
  ) {
    return null
  }

  const hasAccess = Boolean(
    organizationPermissionsInfo.data?.organizationDetails?.permissions?.sites
      ?.create
  )

  if (!hasAccess) {
    return (
      <main>
        <SiteCreationWizardHeader
          organizationId={organizationId}
          subHeadingText={text.headers.unauthenticatedWizardSubheading}
        />
        <Spacer size={10} />
        <div css={positioningCss}>
          <EmptyState
            heading={text.headers.noAccessTitle}
            headingAs="h2"
            text={text.messages.noAccessDescription}
            primaryAction={
              <EmptyStatePrimaryAction
                to={getPathToOrgDetailsTab(organizationId, OrgDetailsTab.Sites)}
              >
                {text.actions.backToWorkspace}
              </EmptyStatePrimaryAction>
            }
            variant="BORDERED"
          />
        </div>
      </main>
    )
  }
  return (
    <main>
      <SiteCreationWizardHeader
        organizationId={organizationId}
        subHeadingText={text.headers.authenticatedWizardSubheading}
      />
      <StandardSingleColumn>
        <div css={siteCardsContainerCss}>
          <div css={siteCardContainerCss}>
            <Heading
              as="h2"
              css={_theme => ({
                display: "flex",
                justifyContent: "space-between",
              })}
            >
              <span
                css={theme => ({
                  display: "flex",
                  alignItems: "center",
                  fontSize: theme.fontSizes[2],
                })}
              >
                <span css={theme => ({ marginRight: theme.space[3] })}>
                  {text.labels.siteTypeImport}
                </span>
                {selectedProvider &&
                  SourceControlProviderIcon({
                    sourceControlProvider: selectedProvider,
                  })}
              </span>
              {selectedProvider ? (
                <Button
                  variant="GHOST"
                  size="M"
                  css={theme => ({
                    fontSize: theme.fontSizes[1],
                    fontWeight: theme.fontWeights.body,
                  })}
                  onClick={() => {
                    setSelectedProvider(undefined)
                    setVcsConfigId("")
                  }}
                >
                  {text.actions.switchToOtherProvider}
                </Button>
              ) : null}
            </Heading>
            <div
              css={
                showOrganizationPicker
                  ? importSiteCardSelectedProviderCss
                  : importSiteCardContentCss
              }
            >
              {showOrganizationPicker &&
              selectedProvider &&
              typeof vcsConfigId !== "undefined" ? (
                <div
                  css={theme => [
                    flexGrowCss(theme),
                    {
                      width: "100%",
                      height: "100%",
                    },
                  ]}
                >
                  <SiteCreationOrganizationPicker
                    selectedProvider={selectedProvider}
                    organizationId={organizationId}
                    vcsConfigId={vcsConfigId}
                  />
                </div>
              ) : (
                <div css={{ maxWidth: `400px`, margin: `0 auto` }}>
                  <Text>{text.labels.selectProvider}</Text>
                  <SourceProviderSelector
                    availableSourceProviders={
                      sourceProviders?.availableSourceProvidersWithVcsConfig
                    }
                    onPremisesGitConnections={onPremisesConnections}
                    onSelect={(sourceProvider, vcsConfigId) => {
                      setSelectedProvider(sourceProvider)
                      if (vcsConfigId) {
                        setVcsConfigId(vcsConfigId)
                      }
                    }}
                    onAddProvider={(sourceProvider, vcsConfigId) => {
                      processSignupAttempt(
                        mapProviderToLoginOption[sourceProvider],
                        {
                          csrfToken: getUserId(),
                          attachProvider: true,
                          errorRedirectUrl: getPathToCreateSite(organizationId),
                          loginRedirectUrl: getPathToCreateSite(organizationId),
                          ...(vcsConfigId && {
                            enterpriseVCSConfigId: vcsConfigId,
                          }),
                        },
                        {}
                      )

                      setSelectedProvider(sourceProvider)

                      if (
                        [
                          SourceControlProvider.GithubEnterprise,
                          SourceControlProvider.GitlabSelfManaged,
                        ].includes(sourceProvider) &&
                        vcsConfigId
                      ) {
                        setVcsConfigId(vcsConfigId)
                      }
                    }}
                  />
                  <div
                    css={theme => ({
                      margin: `${theme.space[7]} auto ${theme.space[8]}`,
                    })}
                  >
                    <Text
                      size="S"
                      css={theme => ({ color: theme.colors.grey[50] })}
                    >
                      <FormattedMessage<never, "link">
                        message={text.messages.learnMore}
                        tags={{
                          link: function(content) {
                            return (
                              <a
                                href="https://support.gatsbyjs.com/hc/en-us/articles/360057505353-Gatsby-Cloud-Github-Permissions"
                                target="_blank"
                                rel="noreferrer noopener"
                                css={theme => ({
                                  color: theme.colors.gatsby,
                                  textDecoration: `none`,
                                })}
                              >
                                {content}
                                <MdLaunch
                                  css={theme => ({
                                    marginLeft: theme.space[2],
                                  })}
                                />
                              </a>
                            )
                          },
                        }}
                      />
                    </Text>
                  </div>
                  {flags.onPremVcsConnection &&
                  onPremisesConnections?.length === 0 ? (
                    <div
                      css={theme => ({
                        display: `flex`,
                        flexDirection: `column`,
                        gap: theme.space[3],
                      })}
                    >
                      <div
                        css={theme => ({
                          display: `flex`,
                          gap: theme.space[3],
                          justifyContent: `center`,
                        })}
                      >
                        <Link
                          to={`${getPathToOrgSettings(
                            organizationId
                          )}?popConnectionModal=true`}
                        >
                          {text.actions.addSelfHostedGithubConnection}
                        </Link>
                      </div>
                      <div
                        css={theme => ({
                          display: `flex`,
                          gap: theme.space[3],
                          justifyContent: `center`,
                        })}
                      >
                        <Link
                          to={`${getPathToOrgSettings(
                            organizationId
                          )}?popConnectionModal=true&sourceType=${
                            SourceControlProvider.GitlabSelfManaged
                          }`}
                        >
                          {text.actions.addSelfHostedGitlabConnection}
                        </Link>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <StarterPicker />
        </div>
      </StandardSingleColumn>
    </main>
  )
}
